import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Loader2,
  CheckCircle2,
  Circle,
  Sparkles,
  BookOpen,
  X,
  Download
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';

// Import section components (to be created)
import EthosSection from './sections/EthosSection';
import AcademicsSection from './sections/AcademicsSection';
import CampusSection from './sections/CampusSection';
import PersonalAnchorSection from './sections/PersonalAnchorSection';
import RhetoricSection from './sections/RhetoricSection';
import BridgeBuilderSection from './sections/BridgeBuilderSection';
import ComposeSection from './sections/ComposeSection';

// Import panels
import CollegeResearchPanel from './panels/CollegeResearchPanel';
import ProgressPanel from './panels/ProgressPanel';
import CollegeNotebook from '../CollegeNotebook/CollegeNotebook';

/**
 * WhySchoolBuilder - 7-section builder for "Why School" and "Why Major" essays
 *
 * Linear progression through:
 * 1. Ethos & Mission
 * 2. Academics & Initiatives
 * 3. Campus & Culture
 * 4. Personal Anchor (with essay interview diversion)
 * 5. Rhetorical Devices
 * 6. Bridge Builder (AI-assisted)
 * 7. Compose & Assembly (draft generation)
 */
export default function WhySchoolBuilder({
  essayId,
  collegeId,
  userId,
  essaySubtype = 'why_school', // 'why_school' | 'why_major'
  onClose,
  onSave
}) {
  // Define the 7 sections
  const sections = [
    {
      key: 'ethos',
      label: essaySubtype === 'why_major' ? 'Program Philosophy' : 'School Ethos & Mission',
      component: EthosSection
    },
    {
      key: 'academics',
      label: essaySubtype === 'why_major' ? 'Curriculum & Research' : 'Academics & Initiatives',
      component: AcademicsSection
    },
    {
      key: 'campus',
      label: essaySubtype === 'why_major' ? 'Major Community' : 'Campus & Culture',
      component: CampusSection
    },
    {
      key: 'personal',
      label: essaySubtype === 'why_major' ? 'Your Journey to This Major' : 'Personal Anchor',
      component: PersonalAnchorSection
    },
    {
      key: 'rhetoric',
      label: 'Strategic Framing',
      component: RhetoricSection
    },
    {
      key: 'bridges',
      label: 'Connection Builder',
      component: BridgeBuilderSection
    },
    {
      key: 'compose',
      label: 'Assemble & Compose',
      component: ComposeSection
    }
  ];

  // State
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [sectionData, setSectionData] = useState({
    ethos: {},
    academics: {},
    campus: {},
    personal: { diversion_dismissed: false },
    rhetoric: {},
    bridges: { rows: [] },
    compose: {}
  });
  const [collegeData, setCollegeData] = useState(null);
  const [essayData, setEssayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saving' | 'saved' | 'error'
  const [sectionsCompleted, setSectionsCompleted] = useState([]);

  // Refs
  const saveTimeoutRef = useRef(null);

  // Load essay and college data on mount
  useEffect(() => {
    loadData();
  }, [essayId, collegeId]);

  // Auto-save with 2s debounce
  useEffect(() => {
    if (!loading && essayId) {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

      setSaveStatus('saving');
      saveTimeoutRef.current = setTimeout(() => {
        autoSave();
      }, 2000);
    }

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [sectionData, currentSectionIndex]);

  // Load essay and college data
  const loadData = async () => {
    try {
      setLoading(true);

      // Load essay data - use maybeSingle() to handle case where essay doesn't exist yet
      const { data: essay, error: essayError } = await supabase
        .from('college_journey_essays')
        .select('*')
        .eq('id', essayId)
        .maybeSingle();

      if (essayError) throw essayError;

      if (essay) {
        setEssayData(essay);

        // Load existing why_school_data if present
        if (essay.why_school_data) {
          console.log('📚 Loading existing Why School data...');
          setSectionData(essay.why_school_data.section_data || sectionData);
          setCurrentSectionIndex(essay.why_school_data.current_section_index || 0);
          setSectionsCompleted(essay.why_school_data.sections_completed || []);
        } else {
          // Initialize existing essay with Why School data
          console.log('🆕 Initializing Why School data for existing essay...');
          await initializeWhySchoolEssay();
        }
      } else {
        // No essay found - create a new one
        console.log('🆕 Creating new Why School essay...');
        await createNewEssay();
      }

      // Load college data
      await loadCollegeData(collegeId);

    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Failed to load essay data');
    } finally {
      setLoading(false);
    }
  };

  // Create new essay in college_journey_essays
  const createNewEssay = async () => {
    try {
      // If essayId is provided and looks like a UUID, try to use it (from essay slot)
      // Otherwise let Supabase generate a new ID
      const insertData = {
        user_id: userId,
        college_id: collegeId,
        essay_type: 'supplemental',
        essay_subtype: essaySubtype,
        why_school_data: {
          current_section_index: 0,
          sections_completed: [],
          section_data: {}
        }
      };

      // Only include ID if it looks like a valid UUID
      if (essayId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(essayId)) {
        insertData.id = essayId;
      }

      const { data: newEssay, error } = await supabase
        .from('college_journey_essays')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      setEssayData(newEssay);
      console.log('✅ Created new Why School essay:', newEssay.id);
    } catch (err) {
      console.error('Error creating essay:', err);
      toast.error('Failed to create essay');
      throw err;
    }
  };

  // Initialize new Why School essay in database
  const initializeWhySchoolEssay = async () => {
    try {
      // Get school name from college_journey_colleges
      const { data: college, error: collegeError } = await supabase
        .from('college_journey_colleges')
        .select('name')
        .eq('id', collegeId)
        .single();

      if (collegeError) throw collegeError;

      // Call helper function
      const { error } = await supabase.rpc('initialize_why_school_essay', {
        p_essay_id: essayId,
        p_college_id: collegeId,
        p_school_name: college.name,
        p_essay_subtype: essaySubtype
      });

      if (error) throw error;

      console.log('✅ Initialized Why School essay');
    } catch (err) {
      console.error('Error initializing essay:', err);
      toast.error('Failed to initialize essay');
    }
  };

  // Load college data (both global and journey)
  const loadCollegeData = async (collegeId) => {
    try {
      // Load from college_journey_colleges
      const { data: journeyCollege, error: journeyError } = await supabase
        .from('college_journey_colleges')
        .select('*')
        .eq('id', collegeId)
        .single();

      if (journeyError) throw journeyError;

      // Load from global_colleges if linked
      let globalCollege = null;
      if (journeyCollege.global_college_id) {
        const { data, error } = await supabase
          .from('global_colleges')
          .select('*')
          .eq('id', journeyCollege.global_college_id)
          .single();

        if (!error) globalCollege = data;
      }

      // Merge both data sources
      setCollegeData({
        ...journeyCollege,
        global: globalCollege
      });

      console.log('🏫 Loaded college data:', journeyCollege.name);
    } catch (err) {
      console.error('Error loading college data:', err);
      toast.error('Failed to load college data');
    }
  };

  // Auto-save to database
  const autoSave = async () => {
    try {
      const { error } = await supabase
        .from('college_journey_essays')
        .update({
          why_school_data: {
            essay_subtype: essaySubtype,
            school_name: collegeData?.name,
            college_id: collegeId,
            sections_completed: sectionsCompleted,
            current_section_index: currentSectionIndex,
            section_data: sectionData
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', essayId);

      if (error) throw error;

      setSaveStatus('saved');
      console.log('💾 Auto-saved');
    } catch (err) {
      console.error('Error auto-saving:', err);
      setSaveStatus('error');
      toast.error('Failed to save progress');
    }
  };

  // Update section data
  const updateSectionData = (sectionKey, newData) => {
    setSectionData(prev => ({
      ...prev,
      [sectionKey]: newData
    }));
  };

  // Export all content as a readable document
  const exportContent = () => {
    const collegeName = collegeData?.name || 'School';
    const date = new Date().toLocaleDateString();

    let content = `${essaySubtype === 'why_major' ? 'Why Major' : 'Why School'} Essay Research\n`;
    content += `${collegeName}\n`;
    content += `Exported: ${date}\n`;
    content += `\n${'='.repeat(60)}\n\n`;

    // Section 1: Ethos
    if (sectionData.ethos) {
      content += `SECTION 1: SCHOOL ETHOS & MISSION\n`;
      content += `${'-'.repeat(60)}\n\n`;
      if (sectionData.ethos.motto) {
        content += `Motto/Mission: ${sectionData.ethos.motto}\n\n`;
      }
      if (sectionData.ethos.mission_elaboration) {
        content += `Mission Elaboration:\n${sectionData.ethos.mission_elaboration}\n\n`;
      }
      if (sectionData.ethos.motto_in_action) {
        content += `What This Looks Like in Action:\n${sectionData.ethos.motto_in_action}\n\n`;
      }
      if (sectionData.ethos.buzzwords?.length > 0) {
        content += `Key Buzzwords:\n`;
        sectionData.ethos.buzzwords.forEach((bw, i) => {
          content += `\n${i + 1}. ${bw.term}\n`;
          if (bw.rephrase) content += `   → ${bw.rephrase}\n`;
        });
        content += `\n`;
      }
      content += `\n`;
    }

    // Section 2: Academics
    if (sectionData.academics) {
      content += `SECTION 2: ACADEMICS & INITIATIVES\n`;
      content += `${'-'.repeat(60)}\n\n`;

      if (sectionData.academics.courses?.length > 0) {
        content += `Courses:\n`;
        sectionData.academics.courses.forEach((c, i) => {
          content += `\n${i + 1}. ${c.title || '(Untitled)'}\n`;
          if (c.description) content += `   Description: ${c.description}\n`;
          if (c.why) content += `   Why: ${c.why}\n`;
        });
        content += `\n`;
      }

      if (sectionData.academics.professors?.length > 0) {
        content += `Professors:\n`;
        sectionData.academics.professors.forEach((p, i) => {
          content += `\n${i + 1}. ${p.name || '(Unnamed)'}\n`;
          if (p.description) content += `   About: ${p.description}\n`;
          if (p.approach) content += `   Approach: ${p.approach}\n`;
        });
        content += `\n`;
      }

      if (sectionData.academics.initiatives?.length > 0) {
        content += `Labs/Initiatives:\n`;
        sectionData.academics.initiatives.forEach((init, i) => {
          content += `\n${i + 1}. ${init.name || '(Unnamed)'}\n`;
          if (init.description) content += `   About: ${init.description}\n`;
          if (init.problem) content += `   Problem Addressed: ${init.problem}\n`;
        });
        content += `\n`;
      }
    }

    // Section 3: Campus
    if (sectionData.campus) {
      content += `SECTION 3: CAMPUS & CULTURE\n`;
      content += `${'-'.repeat(60)}\n\n`;

      if (sectionData.campus.spaces?.length > 0) {
        content += `Campus Spaces:\n`;
        sectionData.campus.spaces.forEach((s, i) => {
          content += `\n${i + 1}. ${s.name || '(Unnamed)'}\n`;
          if (s.description) content += `   About: ${s.description}\n`;
          if (s.behavior_it_invites) content += `   Behavior It Invites: ${s.behavior_it_invites}\n`;
        });
        content += `\n`;
      }

      if (sectionData.campus.clubs?.length > 0) {
        content += `Clubs/Organizations:\n`;
        sectionData.campus.clubs.forEach((c, i) => {
          content += `\n${i + 1}. ${c.name || '(Unnamed)'}\n`;
          if (c.description) content += `   About: ${c.description}\n`;
          if (c.community_signal) content += `   Community Signal: ${c.community_signal}\n`;
        });
        content += `\n`;
      }

      if (sectionData.campus.location_advantage) {
        content += `Location Advantage:\n${sectionData.campus.location_advantage}\n\n`;
      }
    }

    // Section 4: Personal
    if (sectionData.personal) {
      content += `SECTION 4: PERSONAL CONNECTION\n`;
      content += `${'-'.repeat(60)}\n\n`;

      if (sectionData.personal.micro_moment) {
        content += `My Micro-Moment:\n${sectionData.personal.micro_moment}\n\n`;
      }

      if (sectionData.personal.motivations?.length > 0) {
        content += `Core Motivations:\n`;
        sectionData.personal.motivations.forEach((m, i) => {
          content += `${i + 1}. ${m}\n`;
        });
        content += `\n`;
      }
    }

    // Section 5: Rhetoric
    if (sectionData.rhetoric) {
      content += `SECTION 5: RHETORICAL DEVICES\n`;
      content += `${'-'.repeat(60)}\n\n`;

      if (sectionData.rhetoric.contrast_device) {
        content += `Contrast Device:\n${sectionData.rhetoric.contrast_device}\n\n`;
      }
      if (sectionData.rhetoric.mission_metaphor) {
        content += `Mission Metaphor:\n${sectionData.rhetoric.mission_metaphor}\n\n`;
      }
      if (sectionData.rhetoric.what_its_not) {
        content += `"What It's Not" Statement:\n${sectionData.rhetoric.what_its_not}\n\n`;
      }
      if (sectionData.rhetoric.form_as_function) {
        content += `Form-as-Function:\n${sectionData.rhetoric.form_as_function}\n\n`;
      }
      if (sectionData.rhetoric.subversive_take) {
        content += `Subversive Take:\n${sectionData.rhetoric.subversive_take}\n\n`;
      }
    }

    // Section 6: Bridges
    if (sectionData.bridges?.rows?.length > 0) {
      content += `SECTION 6: BRIDGE SENTENCES\n`;
      content += `${'-'.repeat(60)}\n\n`;
      sectionData.bridges.rows.forEach((bridge, i) => {
        content += `Bridge #${i + 1}:\n`;
        if (bridge.school_element) content += `  School Element: ${bridge.school_element}\n`;
        if (bridge.my_experience) content += `  My Experience: ${bridge.my_experience}\n`;
        if (bridge.bridge_sentence) content += `  Bridge: ${bridge.bridge_sentence}\n`;
        content += `\n`;
      });
    }

    // Section 7: Compose
    if (sectionData.compose) {
      content += `SECTION 7: COMPOSITION\n`;
      content += `${'-'.repeat(60)}\n\n`;

      if (sectionData.compose.opening_hook) {
        content += `Opening Hook:\n${sectionData.compose.opening_hook}\n\n`;
      }
      if (sectionData.compose.closing_line) {
        content += `Closing Line:\n${sectionData.compose.closing_line}\n\n`;
      }
      if (sectionData.compose.draft) {
        content += `Draft:\n${sectionData.compose.draft}\n\n`;
      }
    }

    // Create and download file
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${collegeName.replace(/[^a-z0-9]/gi, '_')}_Why_School_Research_${date.replace(/\//g, '-')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Research exported!');
  };

  // Check if section is complete
  const isSectionComplete = (sectionKey) => {
    const data = sectionData[sectionKey];

    switch (sectionKey) {
      case 'ethos':
        return data.motto && data.motto_in_action && data.buzzwords?.length > 0;

      case 'academics':
        return (data.courses?.length > 0 || data.professors?.length > 0 || data.initiatives?.length > 0);

      case 'campus':
        return (data.spaces?.length > 0 || data.clubs?.length > 0);

      case 'personal':
        return data.micro_moment && data.core_motivations?.length >= 2;

      case 'rhetoric':
        return data.contrast && data.mission_metaphor && data.what_its_not;

      case 'bridges':
        return data.rows?.length >= 2 && data.rows.every(b => b.bridge_sentence);

      case 'compose':
        return data.style && data.closing_line;

      default:
        return false;
    }
  };

  // Navigate to section
  const goToSection = (index) => {
    // Save current progress immediately
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    autoSave();

    // Update completed sections
    if (isSectionComplete(sections[currentSectionIndex].key)) {
      setSectionsCompleted(prev => {
        if (!prev.includes(currentSectionIndex)) {
          return [...prev, currentSectionIndex].sort((a, b) => a - b);
        }
        return prev;
      });
    }

    setCurrentSectionIndex(index);
  };

  // Navigate to next section
  const handleContinue = () => {
    if (currentSectionIndex < sections.length - 1) {
      goToSection(currentSectionIndex + 1);
    }
  };

  // Navigate to previous section
  const handleBack = () => {
    if (currentSectionIndex > 0) {
      goToSection(currentSectionIndex - 1);
    }
  };

  // Close builder
  const handleClose = () => {
    // Save before closing
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    autoSave();

    if (onClose) onClose();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        <span className="ml-3 text-gray-600">Loading Why School builder...</span>
      </div>
    );
  }

  const currentSection = sections[currentSectionIndex];
  const CurrentSectionComponent = currentSection.component;

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* College Notebook - Toggleable reference panel */}
      {collegeId && (
        <CollegeNotebook
          collegeId={collegeId}
          essaySlotId={null}
          builderSection={currentSection?.key}
          collegeData={collegeData}
          essaySubtype={essaySubtype}
        />
      )}

      {/* Header */}
      <div className="border-b border-gray-200 bg-white shadow-sm">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {essaySubtype === 'why_major' ? 'Why Major' : 'Why School'} Essay Builder
                </h1>
                <p className="text-sm text-gray-600 mt-0.5">
                  {collegeData?.name} • {currentSection.label}
                </p>
              </div>
            </div>

            {/* Save status and Export button */}
            <div className="flex items-center space-x-3">
              <button
                onClick={exportContent}
                className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition text-sm font-medium text-gray-700"
                title="Export all your research as a text file"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Export</span>
              </button>

              {saveStatus === 'saving' && (
                <div className="flex items-center text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </div>
              )}
              {saveStatus === 'saved' && (
                <div className="flex items-center text-sm text-green-600">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Saved
                </div>
              )}
              {saveStatus === 'error' && (
                <div className="flex items-center text-sm text-red-600">
                  <Circle className="w-4 h-4 mr-2" />
                  Error saving
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Two-column layout - Responsive */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Center Panel - Writing Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8">
            {/* Section Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 font-semibold">
                  {currentSectionIndex + 1}
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {currentSection.label}
                </h2>
              </div>

              {/* Progress indicator */}
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <span>Section {currentSectionIndex + 1} of {sections.length}</span>
                <span>•</span>
                <span>{sectionsCompleted.length} completed</span>
              </div>
            </div>

            {/* Current Section Component */}
            <CurrentSectionComponent
              data={sectionData[currentSection.key]}
              onChange={(newData) => updateSectionData(currentSection.key, newData)}
              collegeData={collegeData}
              essayId={essayId}
              userId={userId}
              allSectionData={sectionData}
              essaySubtype={essaySubtype}
            />

            {/* Navigation */}
            <div className="mt-12 pt-8 border-t border-gray-200 flex items-center justify-between">
              <button
                onClick={handleBack}
                disabled={currentSectionIndex === 0}
                className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Previous</span>
              </button>

              {currentSectionIndex < sections.length - 1 ? (
                <button
                  onClick={handleContinue}
                  className="flex items-center space-x-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                >
                  <span>Continue</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleClose}
                  className="flex items-center space-x-2 px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Finish & Save</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Progress (hidden on mobile, shown on tablet+) */}
        <div className="hidden lg:block lg:w-72 xl:w-80 border-l border-gray-200 overflow-y-auto bg-gray-50">
          <ProgressPanel
            sections={sections}
            currentIndex={currentSectionIndex}
            sectionsCompleted={sectionsCompleted}
            sectionData={sectionData}
            onNavigate={goToSection}
            isSectionComplete={isSectionComplete}
          />
        </div>
      </div>
    </div>
  );
}
